import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SmsConversation, SmsMessage } from '@marnic/shared';
import { useSms } from '../store/sms';

export interface ConversationsResponse {
  total: number;
  items: SmsConversation[];
}

export interface MessagesResponse {
  items: SmsMessage[];
}

export interface SendSmsResponse {
  ok: boolean;
  conversationId: string;
  message: SmsMessage;
}

export function useSmsConversations(filters: { status?: string; leadId?: string } = {}) {
  const setConversations = useSms((s) => s.setConversations);
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.leadId) params.set('leadId', filters.leadId);

  const query = useQuery({
    queryKey: ['sms', 'conversations', filters.status ?? '', filters.leadId ?? ''],
    queryFn: () => api.get<ConversationsResponse>(`/sms/conversations?${params.toString()}`),
    staleTime: 15_000,
  });

  useEffect(() => {
    if (query.data) setConversations(query.data.items);
  }, [query.data, setConversations]);

  return query;
}

export function useSmsMessages(conversationId: string | null) {
  const setMessages = useSms((s) => s.setMessages);

  const query = useQuery({
    queryKey: ['sms', 'messages', conversationId ?? ''],
    queryFn: () =>
      api.get<MessagesResponse>(`/sms/conversations/${conversationId}/messages?limit=100`),
    enabled: !!conversationId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.data && conversationId) setMessages(conversationId, query.data.items);
  }, [query.data, conversationId, setMessages]);

  return query;
}

export function usePatchSmsConversation(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { status?: 'open' | 'closed' | 'snoozed'; agentId?: string | null; markRead?: boolean }) =>
      api.patch<{ ok: boolean }>(`/sms/conversations/${conversationId}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sms', 'conversations'] });
    },
  });
}

export function useSendSms() {
  const qc = useQueryClient();
  const addOutboundMessage = useSms((s) => s.addOutboundMessage);
  return useMutation({
    mutationFn: (data: { leadId?: string; phone?: string; body: string; from?: string }) =>
      api.post<SendSmsResponse>('/sms/send', data),
    onSuccess: (res) => {
      if (res?.message) addOutboundMessage(res.conversationId, res.message);
      void qc.invalidateQueries({ queryKey: ['sms', 'conversations'] });
    },
  });
}
